import React, { useEffect, useState } from 'react';
import GridColumn from '@navi/web-ui/lib/layouts/Grid/GridColumn/GridColumn';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import { Drawer } from '@navi/web-ui/lib/primitives';
import cx from 'classnames';
import { AgTable, Pagination } from '@navi/web-ui/lib/components';
import styles from './UploadHistoryDrawer.module.scss';
import { DropDownPosition, FILE_STATUS } from '../../AmeyoUtility/types';
import { GenericObject } from '@cp/src/types/CommonConstans';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { noop } from '@navi/web-ui/lib/utils/common';
import { poll, PollStatus } from '@cp/src/utils/polling';
import { setUploadHistory } from './reducers/uploadHistorySlice';
import { isFunction } from '@cp/src/utils/commonUtils';
import { GLOBAL } from '@cp/src/constants/Global';
import { fetchUploadHistory } from './actions/uploadHistoryDrawerActions';

interface UploadHistoryComponentProps {
  showUploadHistoryComponent: boolean;
  setShowUploadHistoryComponent: React.Dispatch<React.SetStateAction<boolean>>;
  data: GenericObject;
  transformRowData: () => Array<GenericObject>;
  columnDefs: any;
  fetchUploadHistoryPolling: (pageNo: number, pageSize: number, type?: string) => void;
  headerText?: string;
  typeOfUpload?: string;
  width?: string;
}

const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_DRAWER_WIDTH = '820px';

const UploadHistoryComponent = (props: UploadHistoryComponentProps) => {
  const {
    showUploadHistoryComponent,
    setShowUploadHistoryComponent,
    data,
    transformRowData,
    columnDefs,
    fetchUploadHistoryPolling,
    headerText,
    typeOfUpload,
    width = DEFAULT_DRAWER_WIDTH
  } = props;

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [rowData, setRowData] = useState<Array<GenericObject>>();
  const [pageNumber, setPageNumber] = useState(DEFAULT_PAGE_NUMBER);
  const pages = useSelector((store: RootState) => store?.uploadHistory?.pages);
  const dispatch = useDispatch();

  useEffect(() => {
    setRowData([...transformRowData()]);
    fetchUploadHistory(DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, typeOfUpload);
  }, [data]);

  const handleOnPageChange = (pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const handleOnPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNumber(1);
  };

  useEffect(() => {
    if (!showUploadHistoryComponent) {
      setPageNumber(DEFAULT_PAGE_NUMBER);
      setPageSize(DEFAULT_PAGE_SIZE);
      return;
    }
    let unsubscribe: Function = noop;
    const pollCallback = (status: PollStatus, response: GenericObject) => {
      if (status === PollStatus.SUCCESS) {
        const isPollingRequired =
          response?.data?.data?.filter(
            (i: GenericObject) =>
              [i?.uploadStatus, i?.status].includes(FILE_STATUS.INPROGRESS) ||
              [i?.uploadStatus, i?.status].includes(FILE_STATUS.INITIATED)
          )?.length > 0;
        dispatch(setUploadHistory(response?.data));
        if (!isPollingRequired && isFunction(unsubscribe)) {
          unsubscribe();
        }
      }
    };

    unsubscribe = poll(
      () => fetchUploadHistoryPolling(pageNumber - 1, pageSize, typeOfUpload),
      data => data,
      GLOBAL.CASE_HISTORY_POLLING_INTERVAL,
      pollCallback
    );

    return () => {
      if (isFunction(unsubscribe)) {
        unsubscribe();
      }
    };
  }, [pageNumber, pageSize, showUploadHistoryComponent]);

  return (
    <div>
      <Drawer
        headerText={headerText}
        onClose={() => setShowUploadHistoryComponent(false)}
        open={showUploadHistoryComponent}
        style={{ width: width }}
        contentClasses="overflow-auto"
        usePadding={false}
      >
        <GridRow className="no-gutters">
          <GridColumn md={12} className={cx(styles.Table__table, 'no-gutters')}>
            <AgTable
              defaultColDef={{
                suppressMovable: true,
                wrapText: true,
                autoHeight: true
              }}
              className="uploadHistoryTable"
              rowData={rowData || []}
              columnDefs={columnDefs}
              headerHeight={28}
              rowHeight={43}
              theme={'alpine'}
              sizeColumnsToFit={true}
              getAgRowStyle={() => {
                return { color: 'var(--grayscale-2)' };
              }}
              paginationWrapperClasses={'paginationWrapperClasses'}
              PaginationComponent={
                <Pagination
                  totalCount={pages?.totalElements || 0}
                  onPageChange={handleOnPageChange}
                  currentPage={pageNumber}
                  onPageSizeChange={handleOnPageSizeChange}
                  pageSize={pageSize}
                  pageNumberDropDownPosition={DropDownPosition.TOP}
                />
              }
            />
          </GridColumn>
        </GridRow>
      </Drawer>
    </div>
  );
};

export default UploadHistoryComponent;
